import {useEffect, useState} from "react";
import {ChartDataModel, ChartModel} from "../models/ChartDataModel";
import {useDashboardContext} from "../dashboardContextProvider";
import {DashboardColors} from "../models/DashboardColors";
import {
    DashboardKompetenceGodkendtPercentageEnum,
    enumIndexMap,
    getMinMaxValue,
    KompetenceGodkendtCounts,
    translateDashboardKompetenceGodkendtPercentageEnum
} from "./DashboardKompetenceGodkendtPercentageEnum";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {getEnums} from "../../../core/helpers/enumToList";
import {GroupByKeyAndApplyName} from "../dashboardUtils";
import {DashboardPieChartModel} from "../models/DashboardPieChartModel";
import {DashboardBrugerKompetenceAktivitetModel} from "./DashboardBrugerKompetenceAktivitetModel";
import {GetKompetenceTimeString, KompetenceDatoKategoriEnum} from "./KompetenceDatoKategoriEnum";
import {useHistory} from "react-router-dom";
import {removeWhiteSpace} from "../../../core/helpers/stringHelper";
import {nameof} from "ts-simple-nameof";
import {InitialFilterModel} from "../../../core/components/reactTable/ReactBaseTable";
import {RoutePaths} from "../../../infrastructure/routes";
import {DashboardListForloebModel} from "../dashboardList/models/DashboardListForloebModel";

const mapToBarChartModel = (chartModel: KompetenceGodkendtCounts[], barName: string): ChartModel => {
    let elements: ChartDataModel[] = [];
    chartModel.forEach((chartModel, index) => {
        elements.push(new ChartDataModel(chartModel.kompetenceGroupCategory.toString(), chartModel.kompetenceGroupCategoryName, chartModel.count, DashboardColors.KompetenceBarChartColor, chartModel.onCellClick))
    });

    return {
        name: barName,
        elements: elements
    };
};

const mapToPieChartModel = (chartModels: DashboardPieChartModel<DashboardBrugerKompetenceAktivitetModel>[], pieName: string): ChartModel => {
    let elementsPie: ChartDataModel[] = [];
    chartModels.forEach((chartModel, index) => {
        elementsPie.push(new ChartDataModel(chartModel.key, chartModel.name, chartModel.models.length, DashboardColors.GetKompetenceAktivitetColor(chartModel.name)))
    })
    return {
        name: pieName,
        elements: elementsPie
    };
};

const useDashboardKompetencer = () => {
    const history = useHistory();
    const [kompetenceBarChart, setKompetenceBarChart] = useState<ChartModel>();
    const [kompetencePieChart, setKompetencePieChart] = useState<ChartModel[]>([]);
    const {state} = useDashboardContext()

    const kompetenceProgressTotalAmount = state.kompetenceProgress.length;
    const kompetenceAktivitetTotalAmount = state.kompetenceAktivitet.length;

    useEffect(() => {
        const calculateKompetenceBarChart = () => {
            const kompetenceProgress = state.kompetenceProgress;
            const kompetenceGodkendtCounts: KompetenceGodkendtCounts[] = [];

            getEnums(DashboardKompetenceGodkendtPercentageEnum).forEach(value => {
                kompetenceGodkendtCounts.push({
                    kompetenceGroupCategory: value,
                    kompetenceGroupCategoryName: translateDashboardKompetenceGodkendtPercentageEnum(value),
                    count: 0,
                    onCellClick: () => handleKompetenceAktivitetCellClick(getMinMaxValue(value)),
                });
            });

            for (const progress of kompetenceProgress) {
                const percentage = progress.percentage;
                if (percentage === 0) {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.NulProcent]].count++;
                } else if (percentage > 0 && percentage <= 25) {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.EnTilFemogtyveProcent]].count++;
                } else if (percentage > 25 && percentage <= 50) {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.SeksogtyveTilHalvtredsProcent]].count++;
                } else if (percentage > 50 && percentage <= 75) {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.EnoghalvtredsTilSeksoghalvfjerdsProcent]].count++;
                } else if (percentage > 75 && percentage <= 99) {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.SeksoghalvfjerdsTilNiOgHalvfemsProcent]].count++;
                } else {
                    kompetenceGodkendtCounts[enumIndexMap[DashboardKompetenceGodkendtPercentageEnum.HundredeProcent]].count++;
                }
            }

            const kompetenceGodkendtPieChart = mapToBarChartModel(kompetenceGodkendtCounts, Localizer.dashboard_godkendtPercent());
            setKompetenceBarChart(kompetenceGodkendtPieChart);

            const handleKompetenceAktivitetCellClick = (minMaxValue: [number, number]) => {
                history.push({
                    pathname: RoutePaths.Dashboard({activeTab: removeWhiteSpace(Localizer.dashboard_listView()) }).url,
                    state: [
                        {
                            initialFilterId: nameof<DashboardListForloebModel>(x => x.kompetenceProgress).toLowerCase(),
                            initialFilterValue: minMaxValue
                        },
                    ] as InitialFilterModel[]
                });
            }
        }
        calculateKompetenceBarChart()
    }, [history, state.kompetenceProgress])

    useEffect(() => {
        const calculateKompetencePieChart = () => {
            const kompetenceAktivitet = state.kompetenceAktivitet;
            const HandleKompetenceAktivitetTid = (kompetenceAktivitet: DashboardBrugerKompetenceAktivitetModel[]): ChartModel[] => {
                const today = new Date();
                for (let aktivitet of kompetenceAktivitet) {
                    if (aktivitet.sidsteAktivitetDato === null || aktivitet.sidsteAktivitetDato === undefined || isNaN(new Date(aktivitet.sidsteAktivitetDato).getTime())) {
                        aktivitet.datoKategori = KompetenceDatoKategoriEnum.IngenAktivitet;
                    } else if (aktivitet.sidsteAktivitetDato >= today.addDays(-7)) {
                        aktivitet.datoKategori = KompetenceDatoKategoriEnum.UnderEnUge;
                    } else if (aktivitet.sidsteAktivitetDato >= today.addMonths(-1)) {
                        aktivitet.datoKategori = KompetenceDatoKategoriEnum.OverEnUge;
                    } else if (aktivitet.sidsteAktivitetDato >= today.addMonths(-3)) {
                        aktivitet.datoKategori = KompetenceDatoKategoriEnum.OverEnMaaned;
                    } else {
                        aktivitet.datoKategori = KompetenceDatoKategoriEnum.OverTreMaaneder;
                    }
                }

                let pieModels: ChartModel[] = []
                const allDateEnum = getEnums(KompetenceDatoKategoriEnum);
                const kompetenceAktivitetGrouped = GroupByKeyAndApplyName(kompetenceAktivitet, x => x.datoKategori.toString(), x => GetKompetenceTimeString(x.datoKategori))
                allDateEnum.forEach(enumValue => {
                    if (!kompetenceAktivitetGrouped[enumValue]) {
                        kompetenceAktivitetGrouped[enumValue] = {
                            key: enumValue.toString(),
                            name: GetKompetenceTimeString(enumValue),
                            items: []
                        };
                    }
                });

                const kompetenceAktivitetPieModel = Object.keys(kompetenceAktivitetGrouped)
                    .map(key => ({
                        key: key,
                        name: kompetenceAktivitetGrouped[key].name,
                        models: kompetenceAktivitetGrouped[key].items,
                    })) as DashboardPieChartModel<DashboardBrugerKompetenceAktivitetModel>[]
                const kompetenceAktivitetPieChart = mapToPieChartModel(kompetenceAktivitetPieModel, Localizer.dashboard_kompetenceAktivitet())
                pieModels.push(kompetenceAktivitetPieChart);
                return pieModels;
            }
            setKompetencePieChart(HandleKompetenceAktivitetTid(kompetenceAktivitet));
        }

        calculateKompetencePieChart();
    }, [state.kompetenceAktivitet])

    return {
        kompetenceBarChart: kompetenceBarChart,
        kompetencePieChart: kompetencePieChart,
        kompetenceProgressIsLoading: state.kompetenceProgressIsLoading,
        kompetenceAktivitetIsLoading: state.kompetenceAktivitetIsLoading,
        kompetenceProgressTotalAmount: kompetenceProgressTotalAmount,
        kompetenceAktivitetTotalAmount: kompetenceAktivitetTotalAmount,

        kompetenceKlarmeldinger: state.kompetenceKlarmeldinger,
        kompetenceKlarmeldingerIsLoading: state.kompetenceKlarmeldingerIsLoading,
    }
}
export default useDashboardKompetencer;
